import { materialColorAssignment } from './materialColors.js';

const sorted = (lines) => [...(lines || [])].sort((a, b) => Number(a.sort_order || 0) - Number(b.sort_order || 0));

export function sheetMaterialLines(sheet = {}) {
  const seen = new Set();
  return [...(sheet.material_lines || []), ...(sheet.orphan_material_lines || []), ...(sheet.process_lines || []).flatMap((p) => p.material_lines || [])].filter((line) => {
    if (seen.has(line.id || line)) return false;
    seen.add(line.id || line);
    return true;
  });
}

// Print-only purchase needs: follow the same direct catalogue relation as the
// delivery note, without adding manufacturing costs or traversing another link.
export function linkedMaterialCostLines(sheet, existing = [], garments = 0) {
  if (!sheet) return [];
  const processes = sheet.process_lines || [];
  const sourceIds = new Set(processes.filter((p) => !p.process?.excludes_manufacturing)
    .map((p) => p.process?.albaran_material_process_id).filter(Boolean));
  const sources = processes.filter((p) => sourceIds.has(p.process_id || p.process?.id));
  const seen = new Set(existing.filter((line) => line.line_type === 'material').map((line) => line.source_line_id));
  return sheetMaterialLines(sheet).flatMap((line) => {
    const parent = sources.find((p) => p.id === line.process_line_id || p.material_lines?.includes(line)
      || (line.id && p.material_lines?.some((m) => m.id === line.id)));
    if (!parent || (line.id && seen.has(line.id))) return [];
    if (line.id) seen.add(line.id);
    const consumption = Number(line.quantity || 0);
    const waste = Number(line.waste_pct || 0);
    const price = Number(line.unit_cost || 0);
    return [{
      line_type: 'material', source_line_id: line.id,
      name_snapshot: line.material?.internal_description || line.material?.code || 'Material',
      unit: line.unit || line.material?.default_unit || '',
      quantity_per_unit: consumption, waste_pct: waste, unit_cost: price,
      total_cost: Number((Number(line.line_cost ?? consumption * (1 + waste / 100) * price) * garments).toFixed(4)),
      sort_order: Number(line.sort_order || 0),
      process_source_line_id: parent.id,
      process_name_snapshot: parent.process?.description || 'Proceso',
      supplier_name_snapshot: line.material_supplier?.supplier?.name || '',
    }];
  });
}

export function materialStudyDetails(line = {}) {
  const source = line.material_supplier || {};
  return {
    material_id: line.material_id || line.material?.id || '',
    supplier_id: source.supplier_id || source.supplier?.id || '',
    material_code: String(line.material?.code ?? ''),
    supplier_code: source.supplier?.erp_code || '',
    supplier_ref: source.supplier_ref || '',
    supplier_description: source.supplier_description || '',
    material_season: line.material?.season_code || '',
    notes: line.notes || '',
    color_usage: line.color_usage || '',
    requires_color: line.requires_color ?? null,
  };
}

export function materialStudyUnit(line, sheet) {
  const processes = sheet?.process_lines || [];
  const parent = processes.find((p) => p.id === line?.process_line_id || p.material_lines?.some((m) => m.id && m.id === line?.id));
  const isFabric = parent && (
    parent.process?.albaran_cutting_layout ||
    /^tejidos?$/i.test(String(parent.process?.description || '').trim()) ||
    processes.some((p) => p.process?.albaran_material_process_id && p.process.albaran_material_process_id === (parent.process_id || parent.process?.id))
  );
  const unit = String(line?.unit || line?.material?.default_unit || '').trim();
  return isFabric || /^(m|mt|mts|mtr|mtrs|metro|metros)\.?$/i.test(unit) ? 'mtrs' : unit;
}

// Cutting follows the delivery note configuration, including its direct material
// source. Stored consumption is metres per garment (no conversion). These quantities never feed purchase needs or manufacturing costs.
export function buildCuttingStudy(sheet, colorLines = []) {
  if (!sheet) return [];
  const processes = sorted(sheet.process_lines);
  const materials = sheetMaterialLines(sheet);
  return processes.filter((p) => p.process?.albaran_cutting_layout && !p.process?.excludes_manufacturing).map((process) => {
    const sourceId = process.process.albaran_material_process_id;
    const sourceLines = processes.filter((p) => p.id === process.id || (sourceId && (p.process_id || p.process?.id) === sourceId));
    const ids = new Set(sourceLines.map((p) => p.id));
    const selected = sorted(materials.filter((m) => ids.has(m.process_line_id) || sourceLines.some((p) => p.material_lines?.some((row) => row.id === m.id))));
    return {
      id: process.id,
      process_id: process.process_id || process.process?.id,
      name: process.process.description || 'Corte',
      materials: selected.map((line) => ({
        ...materialStudyDetails(line),
        id: line.id,
        name: line.material?.internal_description || 'Material',
        supplier_name: line.material_supplier?.supplier?.name || '',
        consumptionMetres: Number(line.quantity || 0),
        colors: colorLines.map((color) => ({
          ...color,
          materialColor: materialColorAssignment(color, line).color,
          colorPending: materialColorAssignment(color, line).pending,
          garments: Number(color.total || 0),
          totalMetres: Number(line.quantity || 0) * Number(color.total || 0),
        })),
        totalMetres: Number(line.quantity || 0) * colorLines.reduce((sum, color) => sum + Number(color.total || 0), 0),
      })),
    };
  });
}
